import { useMemo } from 'react';
import useStores from '@stores/useStores';
import { useActionGuard, ActionType, ResourceType } from '@hooks/useActionGuard';
import { constructPermissionResult } from '@hooks/useActionGuard/utils';
import { getMessageList } from '@lib/utils/getMessageList';
import { ApiAccount } from '@api';

/**
 * Custom hook that computes delete permissions for a list of destination accounts.
 * This hook pre-computes permissions at the component level to avoid calling hooks
 * inside loops, which violates React's Rules of Hooks.
 *
 * @param accounts - Array of accounts to check permissions for
 * @returns A map of account IDs to their delete permission results
 */
export const useDeleteAccountPermissions = (
  accounts: ApiAccount[],
  resourceType: 'destination' | 'source',
) => {
  const { destinationsListStore, sourcesListStore } = useStores();
  const { canPerformAction } = useActionGuard();

  const deleteAccountPermission = constructPermissionResult(
    canPerformAction({
      new: {
        actionType: ActionType.CREATE,
        resource: {
          type:
            resourceType === 'destination' ? ResourceType.DESTINATION : ResourceType.EVENT_STREAM,
        },
      },
      old: { resourceType: 'workspace', action: 'edit' },
    }).isAllowed,
    `You do not have permission to delete ${resourceType} accounts.`,
  );

  const deletePermissionsMap = useMemo(
    () =>
      Object.fromEntries(
        accounts.map((account) => {
          const hasLinkedResources =
            resourceType === 'destination'
              ? destinationsListStore.destinations.some(
                  (resource) => resource.config.rudderAccountId === account.id,
                )
              : sourcesListStore.sources.some(
                  (resource) => resource.config.rudderAccountId === account.id,
                );

          return [
            account.id,
            {
              isAllowed: deleteAccountPermission.isAllowed && !hasLinkedResources,
              denialReason: getMessageList([
                deleteAccountPermission.denialReason,
                hasLinkedResources
                  ? `You cannot delete an account with linked ${resourceType}s.`
                  : '',
              ]),
            },
          ];
        }),
      ),
    [accounts],
  );

  return { deletePermissionsMap };
};
